import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Database, Save, Clock, FileText, RefreshCw } from 'lucide-react';
import { buildApiUrl, API_CONFIG } from '../config/api-config';

interface PersistenceStats {
  totalConfigs: number;
  dataFile: string;
  backupDir: string;
  lastBackup: string | null;
}

export function PersistenceStats() {
  const [stats, setStats] = useState<PersistenceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/games/storm/persistence/stats'));
      const result = await response.json();
      
      if (result.success) {
        setStats(result.stats);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error loading persistence stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const forceSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/games/storm/persistence/save'), {
        method: 'POST'
      });
      const result = await response.json();
      
      if (result.success) {
        await loadStats(); // Recharger les stats après sauvegarde
      }
    } catch (error) {
      console.error('Error force saving:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString();
  };

  const formatPath = (path: string) => {
    return path.split('\\').pop() || path.split('/').pop() || path;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Database className="h-5 w-5" />
          Statistiques de Persistance
        </h3>
        <div className="flex gap-2">
          <Button
            onClick={loadStats}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={forceSave}
            disabled={loading}
            size="sm"
          >
            <Save className="h-4 w-4" />
            Sauvegarder
          </Button>
        </div>
      </div>

      {loading && !stats && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          <p className="mt-2 text-sm text-gray-600">Chargement des statistiques...</p>
        </div>
      )}

      {stats && (
        <div className="space-y-4">
          {/* Statistiques principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.totalConfigs}</div>
              <div className="text-sm text-gray-600">Configurations</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats.lastBackup ? '✅' : '❌'}
              </div>
              <div className="text-sm text-gray-600">Backup</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">30s</div>
              <div className="text-sm text-gray-600">Auto-save</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">10</div>
              <div className="text-sm text-gray-600">Backups max</div>
            </div>
          </div>

          {/* Détails des fichiers */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
              <FileText className="h-4 w-4 text-gray-600" />
              <div className="flex-1">
                <div className="font-medium text-sm">Fichier de données</div>
                <div className="text-xs text-gray-500">{formatPath(stats.dataFile)}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
              <Database className="h-4 w-4 text-gray-600" />
              <div className="flex-1">
                <div className="font-medium text-sm">Dossier de backup</div>
                <div className="text-xs text-gray-500">{formatPath(stats.backupDir)}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
              <Clock className="h-4 w-4 text-gray-600" />
              <div className="flex-1">
                <div className="font-medium text-sm">Dernier backup</div>
                <div className="text-xs text-gray-500">{formatDate(stats.lastBackup)}</div>
              </div>
            </div>
          </div>

          {/* Statut de persistance */}
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium text-green-800">Persistance Active</span>
            </div>
            <p className="text-sm text-green-700 mt-1">
              Les configurations sont automatiquement sauvegardées toutes les 30 secondes et à la fermeture du serveur.
            </p>
          </div>

          {/* Dernière mise à jour */}
          {lastUpdate && (
            <div className="text-xs text-gray-500 text-center">
              Dernière mise à jour: {lastUpdate.toLocaleString()}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500">
        <p><strong>Auto-save:</strong> Sauvegarde automatique toutes les 30 secondes</p>
        <p><strong>Backup:</strong> Création automatique de backup avant chaque sauvegarde</p>
        <p><strong>Récupération:</strong> Possibilité de restaurer depuis les backups</p>
      </div>
    </Card>
  );
}
